import { createContext, Dispatch, FC, SetStateAction, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Context } from "../../index";
import "../../pages/Main/MainPage.css";
import "../../pages/UserMonitor/UserMonitor.css";
import "../../component/Project/Tasks/TasksWidget.css";
import { AppContext } from "../../App";
import UserMonitorToolbar from "../../component/UserMonitor/ToolBar/UserMonitorToolbar";
import { FilterOptionsExtented, SelectedOptions } from "../../models/FilterModels/IFilterModel";
import TasksTable from "../../component/UserMonitor/TasksTable/TasksTable";
import TaskDescription from "../../component/UserMonitor/TaskDescription/TaskDescription";
import TasksInfo from "../../component/UserMonitor/TasksInfo/TasksInfo";
import { scrollYCheck } from "../../helpers/domUtils";
import axios from "axios";
import { observer } from "mobx-react-lite";
import { ISelectedFastFilters } from "../../contexts/BoardContext";
import { IGetTaskSort } from "../../common/api_interfaces/task/IGetTaskSort";
import { IMonitorTaskDisplay } from "../../common/api_interfaces/monitor/IUserMonitorTaskFullInfo";
import { IMonitorTasksFilter } from "../../common/api_interfaces/monitor/IMonitorTasksFilter";
import { IIParametrsFilter } from "../../common/api_interfaces/filter/IIParametrsFilter";

export interface ISortOption {
    lable: string;
    sort: "asc" | "desc" | undefined;
    code: string;
}

interface IUserMonitorContext {
    selectedFastFilters: ISelectedFastFilters;
    changeFastFilters: (filters: ISelectedFastFilters) => void;
    filters: FilterOptionsExtented | undefined;
    selectedFilters: SelectedOptions;
    setSelectedFilters: Dispatch<SetStateAction<SelectedOptions>>;
    resetFilter: () => void;
}

export const UserMonitorContext = createContext<IUserMonitorContext>({
    selectedFastFilters: { priority: undefined, type: undefined, tag: undefined },
    changeFastFilters: () => {/* noop */ },
    filters: undefined,
    selectedFilters: {},
    setSelectedFilters: () => {/* noop */ },
    resetFilter: () => {/* noop */ },
});

const TABLE_PAGE_SIZE = 20;

const UserMonitor: FC = () => {
    const { store } = useContext(Context);
    const { setBreadCrumb, setNavPanelHighlight, showToast } = useContext(AppContext);

    const [tasks, setTasks] = useState<IMonitorTaskDisplay[]>([]);
    const [selectedTaskId, setSelectedTaskId] = useState<number | undefined>(undefined);
    const [favouriteTaskIds, setFavouriteTaskIds] = useState<number[]>([]);
    const [skip, setSkip] = useState(0);
    const [scrollEndedTasks, setScrollEndedTasks] = useState<boolean>(false);

    const [taskNameSearch, setTaskNameSearch] = useState<string>("");
    const [selectedFastFilters, setSelectedFastFilters] = useState<ISelectedFastFilters>({
        priority: undefined,
        type: undefined,
        tag: undefined,
    });
    const [filters, setFilters] = useState<FilterOptionsExtented>();
    const [selectedFilters, setSelectedFilters] = useState<SelectedOptions>({});
    const [forceFilterChange, setForceFilterChange] = useState<boolean>(false);

    const initialSortOptions: ISortOption[] = useMemo(() => [
        { lable: "НОМЕР", sort: undefined, code: "idForView" },
        { lable: "НАЗВАНИЕ ЗАДАЧИ", sort: undefined, code: "nameTask" },
        { lable: "ПРОЕКТ", sort: undefined, code: "projectTask" },
        { lable: "КОЛОНКА", sort: undefined, code: "columnTask" },
        { lable: "СТАТУС", sort: undefined, code: "status" },
        { lable: "ПРИОРИТЕТ", sort: undefined, code: "priority" },
        { lable: "ТИП ЗАДАЧИ", sort: undefined, code: "type" },
        { lable: "ДАТА НАЧАЛА", sort: undefined, code: "date" },
        { lable: "ДАТА ОКОНЧАНИЯ", sort: undefined, code: "date" },
    ], []);

    const [dataSort, setDataSort] = useState<ISortOption[]>(initialSortOptions);

    const [showOnlyFavourite, setShowOnlyFavourite] = useState<boolean>(store.userMonitorShowFavourite);
    const [showCompleted, setShowCompleted] = useState<boolean>(false);

    const [totalStats, setTotalStats] = useState<
        { total: number; ongoing: number; completed: number; overdue: number } | undefined
    >(undefined);

    const setShowOnlyFavouriteFunc = useCallback((value: boolean) => {
        setSelectedTaskId(undefined);
        setShowOnlyFavourite(value);
        store.setUserMonitorShowFavourite(value);
    }, [store]);

    const setShowCompletedFunc = useCallback((value: boolean) => {
        setSelectedTaskId(undefined);
        setShowCompleted(value);
    }, []);

    const getCurrentFilters = useCallback((): IMonitorTasksFilter => {
        return {
            responsible: undefined,
            date: selectedFilters.dates?.map((date) => date.toISOString()),
            status: undefined,
            priority: selectedFilters.priorityIds?.length ? selectedFilters.priorityIds : undefined,
            typeTask: selectedFilters.typeTaskIds?.length ? selectedFilters.typeTaskIds : undefined,
            projectIds: selectedFilters.projectIds?.length ? selectedFilters.projectIds : undefined,
            name: taskNameSearch,
            favourite: showOnlyFavourite,
            onlyOngoing: !showCompleted,
        };
    }, [selectedFilters, taskNameSearch, showOnlyFavourite, showCompleted]);

    const getCurrentSort = useCallback((): IGetTaskSort => {
        const sortOption = dataSort.find((s) => s.sort);

        return {
            date: sortOption?.code === "date" ? sortOption.sort : undefined,
            status: sortOption?.code === "status" ? sortOption.sort : undefined,
            type: sortOption?.code === "type" ? sortOption.sort : undefined,
            priority: sortOption?.code === "priority" ? sortOption.sort : undefined,
            idForView: sortOption?.code === "idForView" ? sortOption.sort : undefined,
            nameTask: sortOption?.code === "nameTask" ? sortOption.sort : undefined,
            project: sortOption?.code === "projectTask" ? sortOption.sort : undefined,
        };
    }, [dataSort]);

    const loadFavouriteTasks = useCallback(async () => {
        try {
            const response = await store.userMonitorService.getFavouriteTasks();
            const res = response.data;
            if (res?.favourited) {
                setFavouriteTaskIds(res.favourited);
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data.message);
            }
        }
    }, [store.userMonitorService, showToast]);

    const loadTasks = useCallback(async () => {
        setScrollEndedTasks(false);

        try {
            await Promise.all([
                loadFavouriteTasks(),
                store.userMonitorService.getTasks({
                    skip: 0,
                    take: TABLE_PAGE_SIZE,
                    filters: getCurrentFilters(),
                    sort: getCurrentSort(),
                }).then(tasksRes => {
                    setTasks(tasksRes.data.tasks);
                    setTotalStats(tasksRes.data.rightBarStats);
                    if (tasksRes.data.count === 0) setScrollEndedTasks(true);
                    setSkip(TABLE_PAGE_SIZE);
                })
            ]);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data.message);
            }
        }
    }, [store.userMonitorService, showToast, getCurrentFilters, getCurrentSort, loadFavouriteTasks]);

    const loadPage = useCallback(async () => {
        try {
            const tasksRes = await store.userMonitorService.getTasks({
                skip,
                take: TABLE_PAGE_SIZE,
                filters: getCurrentFilters(),
                sort: getCurrentSort(),
            });

            setTasks((prev) => [...prev, ...tasksRes.data.tasks]);
            if (tasksRes.data.count === 0) setScrollEndedTasks(true);
            setSkip(prev => prev + TABLE_PAGE_SIZE);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                showToast(err.response?.data.message);
                setScrollEndedTasks(true);
            }
        }
    }, [skip, store.userMonitorService, showToast, getCurrentFilters, getCurrentSort]);

    useEffect(() => {
        const initializeComponent = async () => {
            setBreadCrumb([{ label: "Монитор пользователя", url: "monitor" }]);
            setNavPanelHighlight("monitor");

            try {
                const filterParams: IIParametrsFilter = {
                    projectId: undefined,
                    taskPriority: true,
                    taskType: true,
                    project: true,
                };

                const response = await store.filterService.getFilters(filterParams);
                const data = response.data;

                const options: FilterOptionsExtented = {
                    priority: data.taskPriority,
                    typeTask: data.taskType,
                    project: data.project,
                    date: true
                }

                setFilters(options);
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    showToast(error.response?.data.message);
                }
            }
        };

        initializeComponent();
    }, [setBreadCrumb, setNavPanelHighlight, showToast, store.filterService]);

    useEffect(() => {
        setSelectedTaskId(undefined);
        loadTasks();
    }, [loadTasks]);

    useEffect(() => {
        if (forceFilterChange) return;

        const hasPriorityFilters = selectedFilters.priorityIds && selectedFilters.priorityIds?.length > 0;
        const hasTypeFilters = selectedFilters.typeTaskIds && selectedFilters.typeTaskIds?.length > 0;

        if (hasPriorityFilters && hasTypeFilters) {
            setSelectedFastFilters({
                priority: undefined,
                type: undefined,
                tag: undefined,
            });
        } else if (hasPriorityFilters) {
            setSelectedFastFilters(prev => ({
                priority: undefined,
                type: prev.type,
                tag: undefined,
            }));
        } else if (hasTypeFilters) {
            setSelectedFastFilters(prev => ({
                priority: prev.priority,
                type: undefined,
                tag: undefined,
            }));
        }
    }, [selectedFilters, forceFilterChange]);

    const changeFastFilters = useCallback((fastFilters: ISelectedFastFilters) => {
        setSelectedFastFilters(fastFilters);
        setSelectedFilters(prev => ({
            ...prev,
            priorityIds: fastFilters.priority ? [fastFilters.priority.id] : undefined,
        }));
        setForceFilterChange(true);
    }, []);

    const resetFilter = useCallback(() => {
        setTaskNameSearch("");
        setSelectedFilters({});
        setSelectedFastFilters({
            priority: undefined,
            type: undefined,
            tag: undefined,
        });
    }, []);

    const updateFavourite = useCallback(async (newFavourite: { favourited: number[] }) => {
        try {
            const response = await store.userMonitorService.setFavouriteTasks(newFavourite);
            const tasksUpdated = response.data;

            if (tasksUpdated?.favourited) {
                setFavouriteTaskIds(tasksUpdated.favourited);
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data.message);
            }
        }
    }, [store.userMonitorService, showToast]);

    const onSortChange = useCallback(() => {
        setDataSort(prev => prev.map(s => ({
            ...s,
            sort: s.sort ? (s.sort === "asc" ? "desc" : undefined) : undefined
        })));
        setSelectedTaskId(undefined);
    }, []);

    const addSort = useCallback((code: string) => {
        setDataSort(prev => prev.map(s => ({
            ...s,
            sort: s.code === code ? "asc" : undefined
        })));
        setSelectedTaskId(undefined);
    }, []);

    const onScroll = useCallback(async (event: React.UIEvent<HTMLDivElement>) => {
        if (scrollYCheck(event, 1) && !scrollEndedTasks) {
            loadPage();
        }
    }, [scrollEndedTasks, loadPage]);

    const contextValue = useMemo(() => ({
        selectedFastFilters,
        changeFastFilters,
        filters,
        selectedFilters,
        setSelectedFilters,
        resetFilter,
    }), [selectedFastFilters, changeFastFilters, filters, selectedFilters, resetFilter]);

    const selectedTask = useMemo(() =>
        tasks.find((t) => t.id === selectedTaskId),
        [tasks, selectedTaskId]);

    return (
        <UserMonitorContext.Provider value={contextValue}>
            <div className="user-monitor-page-main-container">
                <div className="user-monitor-main-widget-area">
                    <div className="user-monitor-top-toolbar card-shadow">
                        <UserMonitorToolbar
                            setShowCompleted={setShowCompletedFunc}
                            setShowOnlyFavourite={setShowOnlyFavouriteFunc}
                            showCompleted={showCompleted}
                            showOnlyFavourite={showOnlyFavourite}
                            taskNameSearch={taskNameSearch}
                            setTaskNameSearch={setTaskNameSearch}
                            onSearchSubmit={loadTasks}
                        />
                    </div>
                    <TasksTable
                        dataSort={dataSort}
                        onSortChange={onSortChange}
                        addSort={addSort}
                        selectedTaskId={selectedTaskId}
                        setSelectedTaskId={setSelectedTaskId}
                        tasks={tasks}
                        favouriteTaskIds={favouriteTaskIds}
                        updateFavourite={updateFavourite}
                        onScroll={onScroll}
                    />
                </div>
                <div className="user-monitor-right-bar-container card-shadow">
                    {selectedTaskId ? (
                        selectedTask ? (
                            <TaskDescription
                                task={selectedTask}
                                setSelectedTaskId={setSelectedTaskId}
                            />
                        ) : null
                    ) : (
                        <TasksInfo totalStats={totalStats} />
                    )}
                </div>
            </div>
        </UserMonitorContext.Provider>
    );
};

export default observer(UserMonitor);